import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { ApiClient } from '../../lib/api/client';
import { setCredentials } from '../../store/slices/authSlice';

interface CartAuthModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  serviceId?: string;
  serviceName?: string;
  returnCatalog?: string;
}

type AuthMode = 'register' | 'login';

interface AddressData {
  street: string;
  city: string;
  state: string;
  zip_code: string;
}

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  companyName: string;
  betaAccessCode: string;
  address: AddressData;
}

interface LoginFormData {
  email: string;
  password: string;
}

export function CartAuthModal({
  open,
  onClose,
  onSuccess,
  serviceId,
  serviceName,
  returnCatalog,
}: CartAuthModalProps) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<AuthMode>('register');

  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: '',
    betaAccessCode: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip_code: '',
    },
  });

  const [loginData, setLoginData] = useState<LoginFormData>({
    email: '',
    password: '',
  });

  const handleInputChange = (field: string, value: string) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof RegisterFormData] as any),
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleCreateAccount = async () => {
    // Validate required fields
    if (
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword ||
      !formData.firstName ||
      !formData.lastName ||
      !formData.betaAccessCode ||
      !formData.address.street ||
      !formData.address.city ||
      !formData.address.state ||
      !formData.address.zip_code
    ) {
      setError('All fields are required');
      return;
    }

    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create the account with address using customer registration endpoint
      const registerData = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        company: formData.companyName || null,
        betaAccessCode: formData.betaAccessCode,
        address: formData.address,
        return_catalog: returnCatalog,
      };

      const response = await ApiClient.post('/auth/register/customer', registerData);

      const token = response?.token;
      if (!token) throw new Error('Registration failed: no token');

      // Dispatch to Redux to update auth state
      dispatch(
        setCredentials({
          token,
          user: response?.user || null,
        }),
      );

      // After successful registration, add the item to cart if serviceId provided
      if (serviceId) {
        try {
          await ApiClient.post('/api/cart/items', {
            marketplace_service_id: serviceId,
            quantity: 1,
            catalog_instance_id: 'aaaaaaaa-bbbb-1111-2222-aaaaaaaaaaaa', // Default main catalog
            session_id: null,
            selected_address_id: null, // Will use the address from registration
            selected_perk_id: null,
            variant_key: null,
            variant_selections: null,
            variant_price: null,
          });

          // Trigger cart refresh
          window.dispatchEvent(new CustomEvent('cartUpdated'));
        } catch {
          // Don't fail the whole flow if cart addition fails
        }
      }

      // Trigger auth state change so the app knows user is logged in
      window.dispatchEvent(new CustomEvent('authStateChanged'));

      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    // Validate required fields
    if (!loginData.email || !loginData.password) {
      setError('Email and password are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await ApiClient.login({
        email: loginData.email,
        password: loginData.password,
      });

      const token = response?.token;
      if (!token) throw new Error('Login failed: no token');

      // Dispatch to Redux to update auth state
      dispatch(
        setCredentials({
          token,
          user: response?.user || null,
        }),
      );

      // After successful login, add the item to cart if serviceId provided
      if (serviceId) {
        try {
          await ApiClient.post('/api/cart/items', {
            marketplace_service_id: serviceId,
            quantity: 1,
            catalog_instance_id: 'aaaaaaaa-bbbb-1111-2222-aaaaaaaaaaaa', // Default main catalog
            session_id: null,
            selected_address_id: null,
            selected_perk_id: null,
            variant_key: null,
            variant_selections: null,
            variant_price: null,
          });

          // Trigger cart refresh
          window.dispatchEvent(new CustomEvent('cartUpdated'));
        } catch {
          // Don't fail the whole flow if cart addition fails
        }
      }

      // Trigger auth state change so the app knows user is logged in
      window.dispatchEvent(new CustomEvent('authStateChanged'));

      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      companyName: '',
      betaAccessCode: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip_code: '',
      },
    });
    setLoginData({
      email: '',
      password: '',
    });
    setAuthMode('register');
    setError(null);
  };

  React.useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          {authMode === 'register' ? 'Create Account to Add to Cart' : 'Login to Add to Cart'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {serviceName ? `Add "${serviceName}" to your cart` : 'Add this service to your cart'}
        </Typography>
        {authMode === 'register' && (
          <Typography
            variant="body2"
            color="primary"
            sx={{ mt: 1, fontWeight: 'bold', cursor: 'pointer' }}
            onClick={() => setAuthMode('login')}
          >
            I already have an account, log in now →
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {authMode === 'register'
              ? 'To add items to your cart and place orders, we need to create your account with a service address. This address will be used as your default service location.'
              : 'Please sign in to add this item to your cart.'}
          </Typography>

          <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
            <Typography variant="body2" fontWeight="bold">
              🚀 Beta Access Required
            </Typography>
            <Typography variant="body2">
              Aithon is currently in beta. You need a valid beta access code to create an account.
            </Typography>
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {authMode === 'login' ? (
            // Login Form
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData((prev) => ({ ...prev, email: e.target.value }))}
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData((prev) => ({ ...prev, password: e.target.value }))}
                  required
                  disabled={loading}
                />
              </Grid>
            </Grid>
          ) : (
            // Registration Form
            <Grid container spacing={3}>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Personal Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Beta Access Code"
                  value={formData.betaAccessCode}
                  onChange={(e) => handleInputChange('betaAccessCode', e.target.value)}
                  required
                  disabled={loading}
                  helperText="Enter the code provided to access the beta"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  required
                  disabled={loading}
                  helperText="Must be at least 8 characters"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  required
                  disabled={loading}
                  helperText="Re-enter your password"
                />
              </Grid>

              {/* Service Address */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, mt: 2 }}>
                  Service Address
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This will be your default address for service delivery and billing.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.address.street}
                  onChange={(e) => handleInputChange('address.street', e.target.value)}
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required disabled={loading}>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={formData.address.state}
                    onChange={(e) => handleInputChange('address.state', e.target.value)}
                    label="State"
                  >
                    <MenuItem value="AL">Alabama</MenuItem>
                    <MenuItem value="AK">Alaska</MenuItem>
                    <MenuItem value="AZ">Arizona</MenuItem>
                    <MenuItem value="AR">Arkansas</MenuItem>
                    <MenuItem value="CA">California</MenuItem>
                    <MenuItem value="CO">Colorado</MenuItem>
                    <MenuItem value="CT">Connecticut</MenuItem>
                    <MenuItem value="DE">Delaware</MenuItem>
                    <MenuItem value="FL">Florida</MenuItem>
                    <MenuItem value="GA">Georgia</MenuItem>
                    <MenuItem value="HI">Hawaii</MenuItem>
                    <MenuItem value="ID">Idaho</MenuItem>
                    <MenuItem value="IL">Illinois</MenuItem>
                    <MenuItem value="IN">Indiana</MenuItem>
                    <MenuItem value="IA">Iowa</MenuItem>
                    <MenuItem value="KS">Kansas</MenuItem>
                    <MenuItem value="KY">Kentucky</MenuItem>
                    <MenuItem value="LA">Louisiana</MenuItem>
                    <MenuItem value="ME">Maine</MenuItem>
                    <MenuItem value="MD">Maryland</MenuItem>
                    <MenuItem value="MA">Massachusetts</MenuItem>
                    <MenuItem value="MI">Michigan</MenuItem>
                    <MenuItem value="MN">Minnesota</MenuItem>
                    <MenuItem value="MS">Mississippi</MenuItem>
                    <MenuItem value="MO">Missouri</MenuItem>
                    <MenuItem value="MT">Montana</MenuItem>
                    <MenuItem value="NE">Nebraska</MenuItem>
                    <MenuItem value="NV">Nevada</MenuItem>
                    <MenuItem value="NH">New Hampshire</MenuItem>
                    <MenuItem value="NJ">New Jersey</MenuItem>
                    <MenuItem value="NM">New Mexico</MenuItem>
                    <MenuItem value="NY">New York</MenuItem>
                    <MenuItem value="NC">North Carolina</MenuItem>
                    <MenuItem value="ND">North Dakota</MenuItem>
                    <MenuItem value="OH">Ohio</MenuItem>
                    <MenuItem value="OK">Oklahoma</MenuItem>
                    <MenuItem value="OR">Oregon</MenuItem>
                    <MenuItem value="PA">Pennsylvania</MenuItem>
                    <MenuItem value="RI">Rhode Island</MenuItem>
                    <MenuItem value="SC">South Carolina</MenuItem>
                    <MenuItem value="SD">South Dakota</MenuItem>
                    <MenuItem value="TN">Tennessee</MenuItem>
                    <MenuItem value="TX">Texas</MenuItem>
                    <MenuItem value="UT">Utah</MenuItem>
                    <MenuItem value="VT">Vermont</MenuItem>
                    <MenuItem value="VA">Virginia</MenuItem>
                    <MenuItem value="WA">Washington</MenuItem>
                    <MenuItem value="WV">West Virginia</MenuItem>
                    <MenuItem value="WI">Wisconsin</MenuItem>
                    <MenuItem value="WY">Wyoming</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={formData.address.zip_code}
                  onChange={(e) => handleInputChange('address.zip_code', e.target.value)}
                  required
                  disabled={loading}
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={authMode === 'login' ? () => setAuthMode('register') : handleCancel}
          disabled={loading}
          variant="outlined"
        >
          {authMode === 'login' ? 'Back to Register' : 'Cancel'}
        </Button>
        <Button
          onClick={authMode === 'login' ? handleLogin : handleCreateAccount}
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading
            ? authMode === 'login'
              ? 'Logging in...'
              : 'Creating Account...'
            : authMode === 'login'
              ? 'Login & Add to Cart'
              : 'Create Account & Add to Cart'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CartAuthModal;
